import * as React from 'react';
import {useState, useEffect} from 'react';
import {ElementLogic, ElementMap} from './types';
import axios from 'axios';
import {AxiosRequestConfig} from 'axios';
import {Form, FormSpy} from 'react-final-form';
import {FormApi} from 'final-form';
import * as dotObject from 'dot-object';
import arrayMutators from 'final-form-arrays';
import {isObject, isEqual} from 'lodash';
import setErrors from './setErrors';
import setFieldData from './setFieldData';
import FormRoot from './FormRoot';
import ElementMapProvider from './ElementMapProvider';
import sanitizeErrors from './sanitizeErrors';

export interface DynamicFormProps {
  formDefinition: ElementLogic[],
  initialValues?: any,
  url?: string,
  onChange?: (v: any, form: FormApi) => void,
  onErrors?: (e: any) => void,
  afterSubmit?: (d: Record<string, any>) => void,
  formatValidation?: (e: any) => any,
  axiosConfig?: AxiosRequestConfig,
  elementMap?: ElementMap,
  form?: FormApi,
}

export const DynamicForm = React.forwardRef<HTMLFormElement, DynamicFormProps>((
  {url, axiosConfig = {}, initialValues, form, onErrors, formatValidation, afterSubmit, onChange, elementMap, formDefinition},
   ref
) => {
  const [oldValues, setOldValues] = useState<any>(null)
  useEffect(() => {
    if (form) form.setConfig('mutators', {...(arrayMutators as any), setErrors, setFieldData});
  }, [form])

  return (
    <Form<Record<string, any>>
      subscription={{}}
      mutators={{...arrayMutators, setErrors, setFieldData}}
      onSubmit={async (data) => {
        if (typeof url === 'string') {
          return await axios({ method: 'POST', url, data, ...axiosConfig})
            .then((resp) => {
              if (afterSubmit) afterSubmit(resp.data);
              return resp;
            })
            .catch((err) => {
              if (onErrors) onErrors(err);
              if (err?.response?.status === 422) {
                const sanitized = sanitizeErrors(err.response.data.errors);
                const dotted = dotObject.object(sanitized);
                return formatValidation ? formatValidation(dotted) : dotted;
              }
              throw err;
          });
        }
      }}
      render={({handleSubmit, form}) => (
        <ElementMapProvider elementMap={elementMap}>
          <form ref={ref} onSubmit={handleSubmit}>
            <FormSpy
              subscription={{values: true, pristine: true}}
              onChange={({values, pristine }) => {
                if (!pristine && onChange && !isEqual(values, oldValues)) {
                  // keep old values for strict equal comparison
                  setOldValues(values);
                  onChange(values, form);
                }
              }} />
            <FormRoot
              form={form}
              initialValues={initialValues}
              formDefinition={formDefinition}
            />
          </form>
        </ElementMapProvider>
      )}
      form={form}
    />
  );
});

const MemoizedDynamicForm = React.memo(DynamicForm, (prevProps, nextProps) => {
  /**
   * The following props should never change:
   * axiosConfig?: AxiosRequestConfig,
   * elementMap?: ElementMap,
   * url?: string,
   * form?: FormApi<Record<string, any>>,
   **/

  /**
   * The following props should only change once:
   * (we check for swapping between defined and undefined)
   * formDefinition: ElementLogic[],
   * initialValues?: any,
   */

  /**
   * These function can change all the time, depending how pure they are defined
   * onChange?: (v: any, form: FormApi<Record<string, any>>) => void,
   * onErrors?: (e: any) => void,
   * afterSubmit?: () => void,
   */

  const sameFormDefs = isObject(prevProps.formDefinition) === isObject(nextProps.formDefinition)
  const sameInitialValues = isObject(prevProps.initialValues) === isObject(nextProps.initialValues)
  return sameFormDefs && sameInitialValues
    && prevProps.onChange === nextProps.onChange
    && prevProps.onErrors === nextProps.onErrors
    && prevProps.afterSubmit === nextProps.afterSubmit
});

export default MemoizedDynamicForm;
